/**
 * NetworkRow — "Explore the SocialPulse Network" tile grid.
 *
 * 12 sister-site tiles, 6 per row on desktop. Each tile carries a
 * purpose-designed inline SVG illustration matching the reference
 * mock (civic building, food stall, hotel, cottage, mountains+lake,
 * cap+books, family silhouette, newspaper, cameras, globe+plane,
 * laptop with </>, hand+heart). All artwork is CSS/SVG only so no
 * external assets are needed.
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PROPERTIES = [
  { key: "civicpulse",  name: "CivicPulse",  lead: "Local government, news and civic engagement.",       to: "/civic",                            tint: "from-emerald-100 to-teal-200",   accent: "text-emerald-700" },
  { key: "foodpulse",   name: "FoodPulse",   lead: "Restaurants, food businesses and local flavors.",     to: "/food-pulse",                       tint: "from-orange-100 to-rose-200",    accent: "text-orange-600" },
  { key: "hotelspulse", name: "HotelsPulse", lead: "Hotels, stays and travel accommodations.",            to: "https://hotelspulse.pro",    external: true, tint: "from-sky-100 to-blue-200",       accent: "text-blue-700" },
  { key: "homespulse",  name: "HomesPulse",  lead: "Real estate, rentals and neighborhood insights.",     to: "/homes",                            tint: "from-violet-100 to-purple-200",  accent: "text-violet-700" },
  { key: "naturepulse", name: "NaturePulse", lead: "Parks, trails, outdoor activities and nature guides.",to: "/nature",                           tint: "from-emerald-100 to-lime-200",   accent: "text-emerald-700" },
  { key: "alumnipulse", name: "AlumniPulse", lead: "Schools, colleges, alumni and learning opportunities.",to: "https://alumnipulse.socialpulse.com", external: true, tint: "from-amber-100 to-orange-200",   accent: "text-amber-700" },
  { key: "familytree",  name: "FamilyTree",  lead: "Family, genealogy and ancestry.",                     to: "https://familytree.team",    external: true, tint: "from-rose-100 to-pink-200",      accent: "text-rose-600" },
  { key: "newspulse",   name: "NewsPulse",   lead: "Local and global news discussions.",                  to: "https://thenewspulse.pro",   external: true, tint: "from-slate-100 to-slate-200",    accent: "text-slate-700" },
  { key: "photopulse",  name: "PhotoPulse",  lead: "Share and explore amazing photos.",                   to: "https://photopulse.pro",     external: true, tint: "from-pink-100 to-fuchsia-200",   accent: "text-fuchsia-600" },
  { key: "worldpulse",  name: "WorldPulse",  lead: "Destinations, travel stories and global connections.",to: "https://worldpulse.pro",     external: true, tint: "from-cyan-100 to-sky-200",       accent: "text-cyan-700" },
  { key: "websitetech", name: "WebsiteTech", lead: "Websites, tech tips and digital tools.",              to: "https://websitetech.pro",    external: true, tint: "from-indigo-100 to-blue-200",    accent: "text-indigo-700" },
  { key: "causespulse", name: "CausesPulse", lead: "Support causes and make a difference.",               to: "/causes",                           tint: "from-rose-100 to-red-200",       accent: "text-rose-600" },
];

export default function NetworkRow() {
  return (
    <section
      className="bg-gradient-to-b from-[#F5F9FE] to-white py-10 sm:py-14"
      data-testid="sp-network-row"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
          <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl">
            Explore the SocialPulse Network
          </h2>
          <Link
            to="/network"
            data-testid="sp-view-entire-network"
            className="text-blue-700 hover:text-blue-900 text-sm font-semibold inline-flex items-center gap-1"
          >
            View Entire Network <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {PROPERTIES.map((p) => <PropertyTile key={p.key} p={p} />)}
        </div>
      </div>
    </section>
  );
}

function PropertyTile({ p }) {
  const inner = (
    <div className="group flex flex-col bg-white rounded-2xl overflow-hidden ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-md transition-all h-full">
      <div className={`relative h-32 bg-gradient-to-br ${p.tint} overflow-hidden`}>
        <PropertyArt kind={p.key} />
      </div>
      <div className="p-4 flex items-start gap-3 flex-1">
        <div className="flex-1 min-w-0">
          <h3 className={`font-serif font-bold text-slate-900 text-[15px] leading-tight ${p.accent}`}>
            {p.name}
          </h3>
          <p className="mt-1 text-[11.5px] leading-snug text-slate-600">
            {p.lead}
          </p>
        </div>
        <span className={`w-7 h-7 grid place-items-center rounded-full bg-slate-50 ring-1 ring-slate-200 ${p.accent} shrink-0 group-hover:bg-blue-50 group-hover:ring-blue-200 transition-colors`}>
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  );

  if (p.external) {
    return (
      <a href={p.to} target="_blank" rel="noopener noreferrer" data-testid={`sp-network-${p.key}`}>
        {inner}
      </a>
    );
  }
  return <Link to={p.to} data-testid={`sp-network-${p.key}`}>{inner}</Link>;
}

/* ─────────────────────────── SVG scenes ─────────────────────────── */

function PropertyArt({ kind }) {
  const Comp = SCENES[kind] || SCENES.civicpulse;
  return (
    <svg viewBox="0 0 200 120" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <Comp />
    </svg>
  );
}

const SCENES = {
  civicpulse: () => (
    <g>
      <rect y="88" width="200" height="32" fill="#059669" opacity="0.25" />
      {/* trees */}
      <circle cx="18"  cy="82"  r="14" fill="#059669" opacity="0.4" />
      <rect   x="15"   y="88"   width="6" height="10" fill="#065f46" opacity="0.55" />
      <circle cx="180" cy="80"  r="14" fill="#059669" opacity="0.4" />
      <rect   x="177"  y="86"   width="6" height="12" fill="#065f46" opacity="0.55" />
      {/* building — pillared civic hall */}
      <polygon points="55,55 145,55 100,32" fill="#1e40af" opacity="0.75" />
      <rect x="52" y="55" width="96" height="8"  fill="#1e40af" opacity="0.85" />
      <rect x="55" y="63" width="90" height="30" fill="#3b82f6" opacity="0.8" />
      {[62, 74, 86, 98, 110, 122, 134].map((x, i) => (
        <rect key={i} x={x} y="65" width="5" height="26" fill="#dbeafe" />
      ))}
      <rect x="55" y="93" width="90" height="5" fill="#1e3a8a" opacity="0.9" />
      {/* flag */}
      <rect x="98" y="16" width="1.5" height="16" fill="#1e3a8a" />
      <polygon points="99.5,16 108,20 99.5,24" fill="#ef4444" />
    </g>
  ),

  foodpulse: () => (
    <g>
      <rect y="90" width="200" height="30" fill="#fb923c" opacity="0.35" />
      {/* awning */}
      <rect x="45" y="46" width="110" height="12" fill="#dc2626" />
      <path d="M45 58 L60 68 L70 58 L85 68 L95 58 L110 68 L120 58 L135 68 L145 58 L155 68 L155 58 Z" fill="#dc2626" opacity="0.9" />
      {/* stall base */}
      <rect x="50" y="68" width="100" height="30" fill="#fef3c7" />
      <rect x="50" y="98" width="100" height="4"  fill="#92400e" opacity="0.8" />
      {/* fruits basket */}
      <rect x="60" y="80" width="24" height="18" fill="#f97316" opacity="0.85" rx="2" />
      <circle cx="65" cy="80" r="4" fill="#dc2626" />
      <circle cx="72" cy="78" r="4" fill="#f59e0b" />
      <circle cx="79" cy="80" r="4" fill="#dc2626" />
      {/* veggies */}
      <circle cx="100" cy="86" r="5" fill="#22c55e" />
      <circle cx="112" cy="84" r="6" fill="#dc2626" />
      <circle cx="126" cy="87" r="5" fill="#f59e0b" />
      <circle cx="138" cy="86" r="4" fill="#22c55e" />
      {/* utensil sign top */}
      <line x1="100" y1="36" x2="100" y2="46" stroke="#dc2626" strokeWidth="2" />
      <circle cx="100" cy="30" r="8" fill="#fef3c7" stroke="#dc2626" strokeWidth="2" />
      <line x1="97" y1="26" x2="97" y2="34" stroke="#dc2626" strokeWidth="1.5" />
      <line x1="103" y1="26" x2="103" y2="34" stroke="#dc2626" strokeWidth="1.5" />
    </g>
  ),

  hotelspulse: () => (
    <g>
      {/* sky */}
      <circle cx="35"  cy="20" r="7"  fill="white" opacity="0.7" />
      <circle cx="45"  cy="18" r="9"  fill="white" opacity="0.6" />
      <circle cx="165" cy="26" r="8"  fill="white" opacity="0.7" />
      <circle cx="175" cy="24" r="6"  fill="white" opacity="0.6" />
      {/* building */}
      <rect x="65" y="30" width="70" height="70" fill="#1e40af" opacity="0.85" />
      <rect x="72" y="30" width="56" height="6" fill="#1e3a8a" />
      <text x="100" y="34.5" fontFamily="Arial" fontSize="4" fontWeight="700" fill="white" textAnchor="middle">HOTEL</text>
      {/* windows grid */}
      {[42, 55, 68, 81].map((y) =>
        [70, 82, 94, 106, 118].map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="8" height="8" fill="#fef3c7" opacity="0.85" />
        ))
      )}
      {/* door */}
      <rect x="94" y="88" width="12" height="14" fill="#7c2d12" />
      <rect x="99" y="94" width="2" height="4" fill="#fbbf24" />
      {/* ground */}
      <rect y="98" width="200" height="22" fill="#93c5fd" opacity="0.4" />
      {/* trees */}
      <circle cx="30" cy="85" r="12" fill="#059669" opacity="0.55" />
      <circle cx="170" cy="88" r="10" fill="#059669" opacity="0.5" />
    </g>
  ),

  homespulse: () => (
    <g>
      {/* sky cloud */}
      <circle cx="50" cy="22" r="10" fill="white" opacity="0.8" />
      <circle cx="60" cy="20" r="12" fill="white" opacity="0.8" />
      <circle cx="70" cy="24" r="8" fill="white" opacity="0.8" />
      {/* trees */}
      <circle cx="20" cy="85" r="14" fill="#059669" opacity="0.4" />
      <circle cx="180" cy="88" r="12" fill="#059669" opacity="0.4" />
      {/* cottage */}
      <polygon points="80,50 120,50 100,25" fill="#a78bfa" />
      <polygon points="100,25 120,50 138,50 118,28" fill="#8b5cf6" />
      <rect x="80" y="50" width="40" height="45" fill="#e0e7ff" />
      <rect x="120" y="50" width="18" height="45" fill="#c7d2fe" />
      {/* windows */}
      <rect x="86" y="58" width="10" height="10" fill="#fef3c7" />
      <line x1="91" y1="58" x2="91" y2="68" stroke="#a78bfa" strokeWidth="1" />
      <line x1="86" y1="63" x2="96" y2="63" stroke="#a78bfa" strokeWidth="1" />
      <rect x="104" y="58" width="10" height="10" fill="#fef3c7" />
      <line x1="109" y1="58" x2="109" y2="68" stroke="#a78bfa" strokeWidth="1" />
      <line x1="104" y1="63" x2="114" y2="63" stroke="#a78bfa" strokeWidth="1" />
      {/* door */}
      <rect x="94" y="75" width="12" height="20" fill="#7c2d12" />
      <circle cx="103" cy="86" r="1" fill="#fbbf24" />
      {/* chimney */}
      <rect x="112" y="28" width="6" height="10" fill="#7c2d12" />
      {/* ground */}
      <rect y="95" width="200" height="25" fill="#86efac" opacity="0.4" />
    </g>
  ),

  naturepulse: () => (
    <g>
      {/* back mountains */}
      <polygon points="0,80 45,35 90,70 130,25 180,60 200,50 200,120 0,120" fill="#3b82f6" opacity="0.35" />
      {/* front mountains */}
      <polygon points="0,95 30,60 65,85 100,55 145,88 200,75 200,120 0,120" fill="#059669" opacity="0.55" />
      {/* peak snow caps */}
      <polygon points="30,60 40,72 20,72" fill="white" opacity="0.85" />
      <polygon points="100,55 112,68 88,68" fill="white" opacity="0.85" />
      {/* lake */}
      <ellipse cx="100" cy="105" rx="70" ry="8" fill="#3b82f6" opacity="0.55" />
      {/* trees */}
      <polygon points="12,90 20,72 28,90" fill="#065f46" />
      <polygon points="35,92 42,78 49,92" fill="#065f46" />
      <polygon points="155,88 163,70 171,88" fill="#065f46" />
      <polygon points="178,90 184,76 190,90" fill="#065f46" />
      {/* sun */}
      <circle cx="160" cy="30" r="10" fill="#fbbf24" opacity="0.75" />
    </g>
  ),

  alumnipulse: () => (
    <g>
      {/* books stack */}
      <rect x="50" y="80" width="100" height="14" fill="#3b82f6" rx="1" />
      <rect x="55" y="66" width="90" height="14" fill="#dc2626" rx="1" />
      <rect x="60" y="52" width="80" height="14" fill="#059669" rx="1" />
      {/* spine details */}
      <rect x="52" y="83" width="96" height="2" fill="white" opacity="0.5" />
      <rect x="57" y="69" width="86" height="2" fill="white" opacity="0.5" />
      <rect x="62" y="55" width="76" height="2" fill="white" opacity="0.5" />
      {/* graduation cap */}
      <polygon points="100,30 70,45 100,55 130,45" fill="#1e293b" />
      <rect x="90" y="55" width="20" height="4" fill="#1e293b" opacity="0.9" />
      {/* tassel */}
      <line x1="130" y1="45" x2="140" y2="38" stroke="#f59e0b" strokeWidth="1.5" />
      <circle cx="141" cy="37" r="3" fill="#f59e0b" />
      {/* button */}
      <circle cx="100" cy="42" r="2" fill="#f59e0b" />
    </g>
  ),

  familytree: () => (
    <g>
      {/* heart above */}
      <path d="M100 30 C 93 22, 82 24, 82 34 C 82 42, 100 52, 100 52 C 100 52, 118 42, 118 34 C 118 24, 107 22, 100 30 Z" fill="#ef4444" opacity="0.8" />
      {/* family silhouettes */}
      {/* parent 1 */}
      <circle cx="62" cy="60" r="8" fill="#e11d48" opacity="0.85" />
      <path d="M50 100 Q 50 74 62 74 Q 74 74 74 100 Z" fill="#e11d48" opacity="0.85" />
      {/* parent 2 */}
      <circle cx="138" cy="60" r="8" fill="#f43f5e" opacity="0.85" />
      <path d="M126 100 Q 126 74 138 74 Q 150 74 150 100 Z" fill="#f43f5e" opacity="0.85" />
      {/* child */}
      <circle cx="100" cy="70" r="6" fill="#fb7185" opacity="0.9" />
      <path d="M92 100 Q 92 80 100 80 Q 108 80 108 100 Z" fill="#fb7185" opacity="0.9" />
      {/* held hands lines */}
      <line x1="70" y1="90" x2="94" y2="94" stroke="#e11d48" strokeWidth="2" opacity="0.7" />
      <line x1="106" y1="94" x2="130" y2="90" stroke="#e11d48" strokeWidth="2" opacity="0.7" />
    </g>
  ),

  newspulse: () => (
    <g>
      {/* newspaper */}
      <rect x="35" y="25" width="130" height="80" fill="white" opacity="0.95" rx="2" />
      <rect x="35" y="25" width="130" height="12" fill="#334155" />
      <text x="100" y="33" fontFamily="Georgia,serif" fontSize="6" fontWeight="700" fill="white" textAnchor="middle" letterSpacing="1">
        DAILY NEWS
      </text>
      {/* headline */}
      <rect x="42" y="42" width="60" height="5" fill="#0f172a" opacity="0.85" rx="1" />
      <rect x="42" y="50" width="45" height="4" fill="#334155" opacity="0.7" rx="1" />
      {/* image block */}
      <rect x="42" y="58" width="45" height="30" fill="#94a3b8" opacity="0.55" rx="1" />
      <polygon points="42,80 55,68 70,78 87,66 87,88 42,88" fill="#64748b" opacity="0.7" />
      <circle cx="80" cy="65" r="3" fill="#fbbf24" opacity="0.9" />
      {/* text lines right */}
      {[58, 65, 72, 79, 86, 93].map((y, i) => (
        <rect key={i} x="94" y={y} width={60 - (i % 2) * 8} height="2.5" fill="#334155" opacity="0.55" rx="1" />
      ))}
      {/* body lines below image */}
      <rect x="42" y="94" width="115" height="2.5" fill="#334155" opacity="0.55" rx="1" />
      <rect x="42" y="99" width="90" height="2.5" fill="#334155" opacity="0.55" rx="1" />
    </g>
  ),

  photopulse: () => (
    <g>
      {/* back camera */}
      <rect x="52" y="45" width="60" height="42" rx="4" fill="#f0abfc" opacity="0.7" />
      <circle cx="82" cy="66" r="12" fill="white" opacity="0.7" />
      <circle cx="82" cy="66" r="8" fill="#a21caf" opacity="0.7" />
      {/* front camera */}
      <rect x="80" y="52" width="70" height="50" rx="5" fill="#ec4899" />
      <rect x="80" y="46" width="20" height="8" rx="1" fill="#be185d" />
      <circle cx="115" cy="77" r="16" fill="white" />
      <circle cx="115" cy="77" r="12" fill="#831843" />
      <circle cx="115" cy="77" r="8"  fill="#0f172a" opacity="0.6" />
      <circle cx="112" cy="74" r="3"  fill="white" opacity="0.65" />
      {/* flash */}
      <rect x="135" y="55" width="8" height="6" rx="1" fill="#fef3c7" />
      {/* photos peeking */}
      <rect x="35" y="30" width="24" height="18" fill="white" stroke="#f0abfc" strokeWidth="1" transform="rotate(-15 47 39)" />
      <rect x="155" y="28" width="24" height="18" fill="white" stroke="#f0abfc" strokeWidth="1" transform="rotate(12 167 37)" />
    </g>
  ),

  worldpulse: () => (
    <g>
      {/* globe */}
      <circle cx="100" cy="65" r="38" fill="#3b82f6" opacity="0.85" />
      <path d="M62 65 Q 100 45, 138 65 Q 100 85, 62 65 Z" fill="none" stroke="white" strokeWidth="1.2" opacity="0.7" />
      <ellipse cx="100" cy="65" rx="16" ry="38" fill="none" stroke="white" strokeWidth="1.2" opacity="0.7" />
      <line x1="62" y1="65" x2="138" y2="65" stroke="white" strokeWidth="1.2" opacity="0.7" />
      {/* continents (blobs) */}
      <path d="M75 50 Q 85 48, 90 55 Q 88 62, 78 60 Q 72 55, 75 50 Z" fill="#065f46" opacity="0.85" />
      <path d="M108 55 Q 122 52, 125 62 Q 118 70, 108 68 Q 104 60, 108 55 Z" fill="#065f46" opacity="0.85" />
      <path d="M82 72 Q 92 74, 94 82 Q 88 88, 80 84 Q 76 78, 82 72 Z" fill="#065f46" opacity="0.85" />
      {/* airplane arc */}
      <path d="M30 40 Q 100 10, 170 40" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.75" />
      <polygon points="168,38 178,35 172,45 170,42 163,45" fill="#dc2626" />
      {/* clouds */}
      <ellipse cx="30" cy="30" rx="10" ry="4" fill="white" opacity="0.75" />
      <ellipse cx="170" cy="90" rx="12" ry="4" fill="white" opacity="0.75" />
    </g>
  ),

  websitetech: () => (
    <g>
      {/* laptop body */}
      <rect x="45" y="35" width="110" height="60" rx="4" fill="#4f46e5" />
      <rect x="52" y="42" width="96" height="46" fill="#1e1b4b" rx="1" />
      {/* code */}
      <text x="100" y="72" fontFamily="'JetBrains Mono', monospace" fontSize="16" fontWeight="700" fill="#a5b4fc" textAnchor="middle">&lt;/&gt;</text>
      {/* code lines */}
      <rect x="58" y="48" width="18" height="2" fill="#fbbf24" opacity="0.85" />
      <rect x="78" y="48" width="25" height="2" fill="#a5b4fc" opacity="0.85" />
      <rect x="58" y="84" width="30" height="2" fill="#22c55e" opacity="0.85" />
      <rect x="90" y="84" width="18" height="2" fill="#fb7185" opacity="0.85" />
      {/* laptop base */}
      <rect x="35" y="95" width="130" height="6" rx="1" fill="#4338ca" />
      <rect x="90" y="93" width="20" height="4" fill="#3730a3" />
      {/* dots corner */}
      <circle cx="55" cy="100" r="1" fill="#f59e0b" />
      <circle cx="145" cy="100" r="1" fill="#f59e0b" />
    </g>
  ),

  causespulse: () => (
    <g>
      {/* palm */}
      <path
        d="M40 90 Q 50 60, 78 62 L 122 62 Q 150 60, 160 90 L 160 105 L 40 105 Z"
        fill="#fca5a5"
        opacity="0.85"
      />
      {/* fingers */}
      <ellipse cx="86" cy="52" rx="6" ry="12" fill="#fca5a5" opacity="0.9" />
      <ellipse cx="100" cy="48" rx="6" ry="15" fill="#fca5a5" opacity="0.9" />
      <ellipse cx="114" cy="52" rx="6" ry="12" fill="#fca5a5" opacity="0.9" />
      {/* wrist */}
      <rect x="60" y="98" width="80" height="7" fill="white" opacity="0.55" />
      {/* heart floating above */}
      <path
        d="M100 35 C 92 25, 78 28, 78 40 C 78 50, 100 65, 100 65 C 100 65, 122 50, 122 40 C 122 28, 108 25, 100 35 Z"
        fill="#dc2626"
      />
      <path
        d="M92 35 C 88 32, 84 34, 84 39 C 84 43, 90 46, 92 47 Z"
        fill="white"
        opacity="0.35"
      />
    </g>
  ),
};
